import { Button, Popover, Modal } from "antd";
import { message } from "utils/ciq-message";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AgGridReact, CustomCellEditorProps } from "ag-grid-react";
import { ColDef, GridOptions } from "ag-grid-enterprise";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD,
  DateFilter,
  DateUtils
} from "utils/dateutils";
import "./project-schedule.scss";
import { ProjectPermissionEnum } from "constants/index";
import { GridLoadingIndicator } from "components/widgets";
import ImportSchedulerTasks from "components/import-schedule-tasks";

import { ScheduleTaskType } from "pages/submittal-schedule-linking/models";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import {
  CellValueRendererWithEditIcon,
  CellChilderenRendererWithEditButton,
  TruncatedTextWithTooltip,
  dummyColumnDef
} from "components/cell-renders";
import CIQDatePicker from "components/custom-date-picker";
import ActiveTagIcon from "components/svg-icons/active-tag-icon";
import { useCIQLazyQuery, useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_UPDATE_PROJECT_SCHEDULE } from "services/graphQL/mutations";
import { QUERY_LIST_OF_IN_PROGRESS_ACTIVATION_SCHEDULES } from "services/graphQL/queries";
import { DeleteOutlined } from "@ant-design/icons";
import { CiqIconBox } from "components/svg-icons";
import CiqLogPageHeader from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { useGetScheduleListData } from "../hooks/common-hooks";

const formatScheduleDate = (date: string) => {
  return DateUtils.format(date, {
    in: "YYYY-MM-DD",
    out: "MM-DD-YYYY"
  });
};

const scheduleDateCellRenderer = (params: any) => {
  const { context, column, data, node } = params;

  const date = data?.schedule_date
    ? formatScheduleDate(data?.schedule_date)
    : "";

  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => {
        context.onEditCell({
          rowIndex: node.rowIndex!,
          colKey: column!.getId(),
          data
        });
      }}
    >
      {date}
    </CellChilderenRendererWithEditButton>
  );
};

type Props = CustomCellEditorProps;

function ScheduleDateCellEditor(props: Props) {
  const { value, onValueChange, stopEditing } = props;

  return (
    <div className="w-full">
      <CIQDatePicker
        className="w-full date-picker-with-icon !text-[13px] [&_input]:text-[13px] [&_input]:[font-feature-settings:'tnum']"
        popupClassName="ag-custom-component-popup"
        placeholder="Select schedule date"
        format={DATE_FORMAT_MMDDYYYY}
        open
        value={DateUtils.dateTimeObj(value)}
        onChange={(newDate: any) => {
          const formattedDate = DateUtils.format(newDate, DATE_FORMAT_YYYYMMDD);
          onValueChange(formattedDate);
          setTimeout(() => stopEditing(), 0);
        }}
        bordered={false}
        allowClear={false}
        data-testid="schedule-date-picker"
      />
    </div>
  );
}

function ActionCellRenderer(params: any) {
  const { data, context } = params;

  const deleteScheduleActionButton = useMemo(() => {
    const button = (
      <div>
        <button
          type="button"
          className={`ciq-icon-button !p-1.5 ${
            data.is_active
              ? "text-ciq-gray-300 hover:text-ciq-gray-300"
              : "text-ciq-red-700 hover:text-ciq-red-700"
          }`}
          onClick={() => {
            context.onScheduleDeleteAction(data);
          }}
          disabled={data.is_active}
          data-testid="delete-schedule-button"
        >
          <DeleteOutlined data-testid="delete-schedule-button-icon" />
        </button>
      </div>
    );

    if (data.is_active) {
      return (
        <Popover
          placement="left"
          content={<div>Cannot delete active schedule</div>}
        >
          {button}
        </Popover>
      );
    }

    return button;
  }, [context, data]);

  return (
    <div className="flex items-center leading-none">
      {deleteScheduleActionButton}
    </div>
  );
}

const scheduleSrNoCellRenderer = (params: {
  node?: { rowIndex?: number | null } | null;
}) => {
  const srNo = (params.node?.rowIndex ?? 0) + 1;
  return <span data-testid="schedule-sr-no">{srNo}</span>;
};

const scheduleNameCellRenderer = (params: any) => {
  const { context, column, data, node } = params;
  const children = (
    <div className="flex items-center gap-1.5 min-w-0">
      <Link
        className="gridNameLink truncate min-w-0"
        to={`/project/${context.projectId}/schedule/project-schedule/${params.data?.import_log_id}`}
        title=""
        data-testid="schedule-name-link"
      >
        <TruncatedTextWithTooltip
          text={params?.data?.schedule_name || ""}
          tooltipContent={params?.data?.schedule_name || ""}
        />
      </Link>
      {params.data?.is_active ? <ActiveTagIcon className="shrink-0" /> : null}
    </div>
  );

  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => {
        context.onEditCell({
          rowIndex: node.rowIndex!,
          colKey: column!.getId(),
          data
        });
      }}
    >
      {children}
    </CellChilderenRendererWithEditButton>
  );
};

export const SOURCE_OPC = "OPC Sync";
export const SOURCE_MANUAL = "Manual Upload";

/**
 * Where the schedule came from. schedule_source_info_id points at the OPC
 * schedule this version was exported from, so it is set for exactly the imports
 * the OPC tab started and null for every file a user uploaded — which makes its
 * presence the whole test, with no status or import_type to consult.
 *
 * Module-level because nothing in it depends on component state; the flag
 * decides whether it is used, not how it behaves.
 */
export const SOURCE_COLUMN_DEF: ColDef = {
  colId: "source",
  field: "schedule_source_info_id",
  headerName: "Source",
  headerTooltip: "Source",
  valueGetter: ({ data }: any) =>
    data?.schedule_source_info_id ? SOURCE_OPC : SOURCE_MANUAL,
  tooltipValueGetter: (param: any) => param.value,
  filter: true,
  // Both values are fixed strings, so the column needs room for the longer one
  // and no more — sizeColumnsToFit honours this floor when it shares out the
  // width, and would otherwise squeeze "Manual Upload" to an ellipsis.
  minWidth: 140,
  maxWidth: 200
};

function ProjectSchedules() {
  const gridRef = useRef<AgGridReact<ScheduleTaskType>>(null);
  const { projectId }: any = useParams();

  const {
    tokenRetrievalState,
    gqlClientForProject,
    tokenContents,
    isFeatureFlagEnabled
  }: TProjectContext = useContext(ProjectContext);

  // Same gate as the Schedules in OPC tab: with the integration off there is no
  // OPC to have synced from, so every row would read "Manual Upload" and the
  // column would only take up space.
  const isOpcIntegrationEnabled = isFeatureFlagEnabled("OPC_INTEGRATION_V1");

  const canEditScheduleDetails = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditScheduleDetails,
    tokenContents?.role ?? ""
  );

  const [isGridReady, setIsGridReady] = useState(false);
  const [scheduleVersions, setScheduleVersions] = useState<Array<any>>();

  const [showImportSchedularDrawer, setshowImportSchedularDrawer] =
    useState(false);

  const [overlayMessage, setOverlayMessage] = useState("");

  const { data: scheduleListData } = useGetScheduleListData();

  const [updateScheduleDetails] = useCIQMutation(
    MUTATION_UPDATE_PROJECT_SCHEDULE,
    {
      client: gqlClientForProject
    }
  );
  const [getInProgressScheduleActivations] = useCIQLazyQuery(
    QUERY_LIST_OF_IN_PROGRESS_ACTIVATION_SCHEDULES
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      dummyColumnDef,
      {
        headerName: "Sr No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        tooltipValueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        cellRenderer: scheduleSrNoCellRenderer,
        width: 80,
        minWidth: 80,
        sortable: true
      },
      {
        colId: "schedule_name",
        field: "schedule_name",
        headerName: "Schedule Name",
        headerTooltip: "Schedule Name",
        // Flexes to fill leftover space after fixed-width date columns.
        flex: 1,
        minWidth: 180,
        cellRenderer: scheduleNameCellRenderer,
        cellClass: () => {
          return canEditScheduleDetails ? "cell-editable" : "";
        },
        editable: canEditScheduleDetails
      },
      {
        colId: "schedule_date",
        field: "schedule_date",
        headerName: "Schedule Date",
        headerTooltip: "Schedule Date",
        width: 150,
        minWidth: 150,
        maxWidth: 150,
        suppressSizeToFit: true,
        comparator: DateFilter.comparator,
        tooltipValueGetter: (param: any) => formatScheduleDate(param.value),
        filter: true,
        cellRenderer: scheduleDateCellRenderer,
        cellClass: () => {
          return canEditScheduleDetails ? "cell-editable" : "";
        },
        editable: canEditScheduleDetails,
        cellEditor: ScheduleDateCellEditor,
        suppressKeyboardEvent: (params: any) => {
          if (params.event.key === "Enter") {
            return true;
          }
          return false;
        }
      },
      {
        colId: "schedule_type",
        field: "schedule_type",
        headerName: "Caption",
        headerTooltip: "Caption",
        valueGetter: (param: any) => {
          const scheduleType = param.data?.schedule_type;
          return scheduleType !== null && scheduleType !== "undefined"
            ? scheduleType
            : "";
        },
        tooltipValueGetter: (param) => param.value,
        cellRenderer: CellValueRendererWithEditIcon,
        cellClass: () => {
          return canEditScheduleDetails ? "cell-editable" : "";
        },
        editable: canEditScheduleDetails
      },
      {
        colId: "uploaded_by",
        field: "uploaded_by",
        headerName: "Uploaded By",
        headerTooltip: "Uploaded By",
        valueGetter: (param: any) => {
          const user = param.data?.import_log?.created_by_user;
          return `${user?.first_name || ""} ${user?.last_name || ""}`;
        },
        tooltipValueGetter: (param: any) => param.value
      },
      {
        colId: "created_at",
        field: "created_at",
        headerName: "Uploaded On",
        headerTooltip: "Uploaded On",
        width: 160,
        minWidth: 160,
        maxWidth: 160,
        suppressSizeToFit: true,
        valueGetter: ({ data }: any) => {
          const createdAt = data?.import_log?.created_at;
          return createdAt ? DateUtils.format(createdAt, "MM-DD-YYYY") : "";
        },
        comparator: DateFilter.comparator,
        tooltipValueGetter: (param: any) => param.value,
        filter: true,
        sort: "desc"
      },
      // Dropped from the column set entirely rather than hidden, mirroring how
      // the flag removes the OPC tab itself.
      ...(isOpcIntegrationEnabled ? [SOURCE_COLUMN_DEF] : []),
      {
        colId: "action",
        headerName: "Actions",
        headerTooltip: "Actions",
        pinned: "right",
        maxWidth: 120,
        cellRenderer: ActionCellRenderer,
        sortable: false,
        filter: false,
        menuTabs: [],
        hide: !canEditScheduleDetails
      }
    ],
    [canEditScheduleDetails, isOpcIntegrationEnabled]
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      autoHeight: true,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const gridOptions: GridOptions = {
    defaultColDef,
    rowSelection: {
      mode: "singleRow",
      checkboxes: false,
      copySelectedRows: false
    },
    // Same Active row tint as Schedules in OPC (shared .schedule-active-row).
    getRowClass: ({ data: row }) =>
      row?.is_active ? "schedule-active-row" : undefined
  };

  const setDrawerAction = (drawerAction: boolean) => {
    setshowImportSchedularDrawer(drawerAction);
  };

  useEffect(() => {
    if (!scheduleListData) return;
    if (scheduleListData?.schedule_import_details?.length === 0) {
      setOverlayMessage("Import a schedule to get started");
      setScheduleVersions([]);
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      setOverlayMessage("No rows to show.");
      const tempArr: any[] = scheduleListData?.schedule_import_details?.sort(
        (v1: any, v2: any) => (v1.is_active > v2.is_active ? -1 : 1)
      );
      setScheduleVersions(tempArr || []);
    }
  }, [scheduleListData]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef?.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  // Re-fit whenever the column set changes, not only on grid-ready and resize.
  //
  // isFeatureFlagEnabled is backed by the entitlements query, so it reads false
  // for the first render or two: the grid fits its columns to the viewport while
  // Source does not exist yet, then Source arrives at its default width on top
  // of a row that already fills the width. The overflow lands past the pinned
  // Actions column, so the column is present but scrolled out of sight — which
  // looks exactly like it was never added.
  useEffect(() => {
    if (!isGridReady) return;
    gridRef.current?.api?.sizeColumnsToFit();
  }, [isGridReady, columnDefs]);

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const onCellEditRequest = useCallback(
    async (params: any) => {
      if (params.colDef.colId !== "schedule_type" && !params.newValue.trim())
        return;

      const existingNodeData = { ...params.data };

      const newNodeData = { ...existingNodeData };

      const payload: any = {
        scheduleDetails: {},
        where: {
          import_log_id: {
            _eq: params.data.import_log_id
          }
        }
      };

      if (params.colDef.colId === "schedule_name") {
        payload.scheduleDetails.schedule_name = params.newValue;
        newNodeData.schedule_name = params.newValue;
      }

      if (params.colDef.colId === "schedule_date") {
        payload.scheduleDetails.schedule_date = params.newValue;
        newNodeData.schedule_date = params.newValue;
      }

      if (params.colDef.colId === "schedule_type") {
        payload.scheduleDetails.schedule_type = params.newValue;
        newNodeData.schedule_type = params.newValue;
      }

      params.node.setData({ ...newNodeData });

      setTimeout(async () => {
        const res = await updateScheduleDetails({
          variables: {
            ...payload
          }
        });

        if (res.success) {
          message.success("Schedule updated successfully");
        } else {
          params.node.setData({ ...existingNodeData });
        }
      });
    },
    [updateScheduleDetails]
  );

  const onScheduleDeleteAction = useCallback(
    (data: any) => {
      Modal.confirm({
        content: "Are you sure you want to delete this schedule?",
        onOk: async () => {
          const oneHourAgo = new Date(
            Date.now() - 60 * 60 * 1000
          ).toISOString();
          const activationResponse = await getInProgressScheduleActivations({
            _created_at_gte: oneHourAgo
          });

          if (activationResponse?.data?.data?.import_log?.length > 0) {
            message.error(
              "A schedule activation is currently in progress. Please try again later."
            );
            return;
          }

          const payload: any = {
            where: {
              import_log_id: {
                _eq: data.import_log_id
              }
            },
            scheduleDetails: {
              deleted: true
            }
          };

          const res = await updateScheduleDetails({
            variables: {
              ...payload
            }
          });

          if (res.success) {
            message.success("Schedule deleted successfully");
          }
        }
      });
    },
    [getInProgressScheduleActivations, updateScheduleDetails]
  );

  const entityName = (
    <div className="flex items-center space-x-2">
      <CiqIconBox feature="SCHEDULES" size={20} padding={8} />
      <div className="text-black text-xl font-semibold pl-1">Schedules</div>
    </div>
  );

  const headerItems = [
    <CiqAgSearch
      key="Schedules_search"
      gridRef={gridRef}
      placeholder="Search Schedules"
      data-testid="schedule-search"
    />,

    <Button
      key="Schedules_Import"
      type="primary"
      hidden={isPermissionNotGrantted(
        ProjectPermissionEnum.ImportSchedule,
        tokenContents?.role ?? ""
      )}
      onClick={() => setshowImportSchedularDrawer(true)}
      data-testid="import-schedule-button"
    >
      Import Schedule
    </Button>
  ];

  return (
    <div className="schedule-versions flex flex-col gap-y-2 h-full">
      <div className="flex flex-col h-full">
        <CiqLogPageHeader
          entityName={entityName}
          titleParam={{
            title: "Schedules",
            totalCount: scheduleVersions?.length,
            filterStats: undefined
          }}
          items={headerItems}
          data-testid="schedule-page-header"
        />
        <div className="flex flex-col h-full ag-theme-alpine">
          <AgGridReact<ScheduleTaskType>
            ref={gridRef}
            rowData={scheduleVersions}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            onGridReady={() => {
              setIsGridReady(true);
              gridRef.current?.api.sizeColumnsToFit();
            }}
            context={{
              onEditCell,
              onScheduleDeleteAction,
              projectId
            }}
            overlayNoRowsTemplate={overlayMessage}
            loadingOverlayComponent={GridLoadingIndicator}
            animateRows={false}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            tooltipShowMode="whenTruncated"
            suppressClickEdit
            stopEditingWhenCellsLoseFocus
            suppressContextMenu
            enableCellTextSelection
            readOnlyEdit
            onCellEditRequest={onCellEditRequest}
          />
        </div>
      </div>

      {showImportSchedularDrawer && (
        <ImportSchedulerTasks
          tokenRetrievalState={tokenRetrievalState}
          setDrawerOpen={setDrawerAction}
          showImportLogDrawer={showImportSchedularDrawer}
          hasActiveSchedule={Boolean(
            scheduleVersions?.some((schedule) => schedule.is_active)
          )}
        />
      )}
    </div>
  );
}

export default ProjectSchedules;
